import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useAuthContext } from '../../context/AuthContext';
import { API } from '../../constant';
import { getToken } from '../../helpers';
import Loading from "../Hooks/Loading";

const COUNTRIES = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
];

const CheckoutForm = ({ plan, clientSecret, customerId }) => {
const { user, isLoading } = useAuthContext();
const [loading, setLoading] = useState(false);
const [message, setMessage] = useState('');
const [error, setError] = useState(null);
const stripe = useStripe();
const elements = useElements();
const submitting = useRef(false);
const navigate = useNavigate();

const [customerName, setCustomerName] = useState('');
const [customerEmail, setCustomerEmail] = useState('');
const [customerAddress, setCustomerAddress] = useState({
    line1: '', city: '', state: '', postal_code: '', country: 'US',
});

useEffect(() => {
    if (!user || isLoading) return;
    setCustomerName(user.username || '');
    setCustomerEmail(user.email || '');
    setCustomerAddress({
        line1: user.address || '',
        city: user.city || '',
        state: user.state || '',
        postal_code: user.postalCode || '',
        country: 'US',
    });
}, [user, isLoading]);

const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submitting.current) return;
    submitting.current = true;
    setLoading(true);
    setError(null);
    const idempotencyKey = `gopremium-${Date.now()}-${user?.id || 'anon'}`;

    const cardElement = elements.getElement(CardElement);

    try {
        // Step 1: Save card via SetupIntent — no charge, no lock conflict
        const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
                card: cardElement,
                billing_details: {
                    name: customerName,
                    email: customerEmail,
                    address: customerAddress,
                },
            },
        });

        if (setupError) {
            setError(setupError.message);
            setLoading(false);
            submitting.current = false;
            return;
        }

        // Step 2: Create subscription using the saved payment method
        const { data } = await axios.post(`${API}/stripe/create-subscription`, {
            idempotencyKey,
            planId: plan.stripePlanId,
            paymentMethodId: setupIntent.payment_method,
            customerId,
        }, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
                },
        });

        if (data.error) {
            setError(data.error.message);
            setLoading(false);
            submitting.current = false;
            return;
        }

        if (data?.subscriptionRecordSaved === false && user?.id) {
            const nextPaymentDate = new Date();
            nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
            try {
                await axios.post(`${API}/subscriptions`, {
                    data: {
                        userId: user.id,
                        subscriptionId: data.subscriptionId,
                        stripeCustomerId: data.customerId,
                        stripePlanId: plan.stripePlanId,
                        status: data.status || 'active',
                        startDate: new Date().toISOString(),
                        nextPaymentDate: nextPaymentDate.toISOString(),
                    },
                }, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
            } catch (subErr) {
                console.warn('Fallback subscription save failed:', subErr);
            }
        }

        // Step 3: Save order to Strapi
        try {
            await axios.post(`${API}/orders`, {
                data: {
                    userId: String(user?.id),
                    amount: plan.amount,
                    status: 'completed',
                    description: plan.name,
                    subscriptionId: data.subscriptionId,
                    chargeStatus: 'succeeded',
                    subscriptionPaymentStatus: 'active',
                },
            }, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
        } catch (orderErr) {
            console.warn('Failed to save order to Strapi:', orderErr);
        }

        // Step 4: Build order details for thank you page
        const orderDetails = {
            customerName,
            customerEmail,
            customerAddress,
            orderSummary: [{ productName: plan.name, quantity: 1, price: plan.amount }],
            paymentDetails: {
                method: 'Credit Card',
                amount: plan.amount,
                transactionId: { id: data.subscriptionId },
            },
        };

        // Step 5: Send order confirmation email (non-blocking)
        try {
            await fetch(`${API}/email/order-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ orderDetails }),
            });
        } catch (emailErr) {
            console.warn('Order confirmation email failed (non-critical):', emailErr);
        }

        setLoading(false);
        navigate('/my-account/order-thank-you', { state: { orderDetails } });

    } catch (err) {
        setError(err.message);
        setMessage(err.message);
        setLoading(false);
        submitting.current = false;
    }
};

return (
<div className="theme-form">
    {loading && <Loading />}
    <div className="register-form">
        <form onSubmit={handleSubmit}>
        <div className="head-title">Billing Details</div>
        <input
            type="text"
            placeholder="Name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            required
        />
        <input
            type="email"
            placeholder="Email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            required
        />
        <input
            type="text"
            placeholder="Address Line 1"
            value={customerAddress.line1}
            onChange={(e) => setCustomerAddress({ ...customerAddress, line1: e.target.value })}
            required
        />
        <input
            type="text"
            placeholder="City"
            value={customerAddress.city}
            onChange={(e) => setCustomerAddress({ ...customerAddress, city: e.target.value })}
            required
        />
        <input
            type="text"
            placeholder="State"
            value={customerAddress.state}
            onChange={(e) => setCustomerAddress({ ...customerAddress, state: e.target.value })}
            required
        />
        <input
            type="text"
            maxLength="5"
            placeholder="Zip Code"
            value={customerAddress.postal_code}
            onChange={(e) => setCustomerAddress({ ...customerAddress, postal_code: e.target.value })}
            required
        />
        <select
            value={customerAddress.country}
            onChange={(e) => setCustomerAddress({ ...customerAddress, country: e.target.value })}
            required
            disabled
        >
            <option value="">Select Country</option>
            {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>{c.name} {c.flag}</option>
            ))}
        </select>
        <div className="payment-details">
            <p className="heading">Payment details</p>
            <CardElement />
        </div>
        <button
            className="btn btn-gradient btn-full btn-white"
            type="submit"
            disabled={!stripe || loading}
        >
            Subscribe
        </button>
        {message && <div className="message success">{message}</div>}
        {error && <div className="message error-message">{error}</div>}
        </form>
    </div>
</div>
);
};

export default CheckoutForm;
